/**
 * GoHighLevel — Appointment Webhook payload TypeBox schemas (Story 7.2).
 *
 * Validated wire shape for `POST /webhook/ghl/appointment`. The webhook
 * delivers `AppointmentCreate`, `AppointmentUpdate` and `AppointmentDelete`
 * events; the route plugin discriminates by the top-level `type` field and
 * routes to the matching handler in `ghl-appointment-processor.ts`.
 *
 * Doc cross-reference:
 *   - `docs/architecture/ghl-calendar-integration.md` §3 (webhooks Ed25519 +
 *     retry up to 3 days)
 *   - GHL marketplace doc — Calendar Webhook Events
 *
 * Constitution Article IV (No Invention): every field traces to the validated
 * GHL doc. `additionalProperties: true` on the payload + appointment
 * projection so a GHL release that adds fields never breaks our parser. The
 * webhook reuses the `GHLAppointment` shape from Story 7.1's REST schemas.
 */
import { Type } from '@sinclair/typebox';
import { GHLAppointment } from './ghl-appointment.js';
// ============================================================================
// Event type — discriminator
// ============================================================================
/**
 * Top-level `type` discriminator GHL emits on the appointment webhook. The
 * three values map 1:1 to the three handlers in the processor:
 *   - AppointmentCreate → handleAppointmentCreate (creates meeting +
 *                         conversation + enqueues confirmation-c1)
 *   - AppointmentUpdate → handleAppointmentUpdate (mutates scheduled_at /
 *                         outcome on the existing meeting)
 *   - AppointmentDelete → handleAppointmentDelete (marks outcome=cancelled,
 *                         tears down C2/C3 jobs)
 */
export const GHLAppointmentEventType = Type.Union([
    Type.Literal('AppointmentCreate'),
    Type.Literal('AppointmentUpdate'),
    Type.Literal('AppointmentDelete'),
], { description: 'GHL appointment webhook event discriminator' });
// ============================================================================
// Webhook payload — POST /webhook/ghl/appointment body
// ============================================================================
/**
 * Top-level webhook payload. Only `type` and `appointment` are mandatory;
 * `locationId` (when present at the top level) is preserved in audit but the
 * canonical location for the appointment lives inside the appointment record
 * itself.
 *
 * `additionalProperties: true` covers GHL extras (timestamp, eventId,
 * companyId, etc.) the route may want to log without re-validating the
 * shape.
 */
export const GHLAppointmentWebhookPayload = Type.Object({
    type: GHLAppointmentEventType,
    locationId: Type.Optional(Type.String()),
    appointment: GHLAppointment,
}, {
    additionalProperties: true,
    description: 'GHL appointment webhook envelope (Create/Update/Delete)',
});
//# sourceMappingURL=ghl-webhook.js.map